import type { Locale } from "@/entities/language";
import type { MenuItemSubmenu, MenuItemSimple } from "@/modules/menu";

import { LoadingOutlined } from "@ant-design/icons";
import { Badge, Flex, Spin } from "antd";
import { useTranslation } from "react-i18next";

import { useReferentialLanguages } from "@/entities/referential/referential-languages";
import { useGetSettings, useUpdateSettings } from "@/entities/settings";
import { IconCustom } from "@/ui/icon-custom";

export const useLanguageMenuItem = (): MenuItemSubmenu => {
  const { t } = useTranslation();

  const { languages } = useReferentialLanguages();
  const { settings, isLoading } = useGetSettings();
  const { mutate, isPending } = useUpdateSettings();

  const loading = isLoading || isPending;

  const updateUserLocale = (locale: Locale) => {
    // Just update settings locale. i18n locale will be updated by the
    // <LanguageHandler> loaded in <App> everytime settings locale is updated
    mutate({ locale });
  };

  const isCurrentLocale = (locale: Locale) => {
    return locale === settings?.locale;
  };

  const subMenu: MenuItemSimple[] = languages.map(({ code, name }) => ({
    key: code,
    label: (
      <Flex gap="small" align="center">
        {name}
        {isCurrentLocale(code) && <Badge status="default" />}
      </Flex>
    ),
    onClick: () => updateUserLocale(code),
    disabled: isCurrentLocale(code),
    style: isCurrentLocale(code)
      ? { color: "inherit", cursor: "default", opacity: 1 }
      : undefined,
  }));

  return {
    key: "language",
    label: t("navigation:language"),
    icon: (
      <Spin
        size="small"
        indicator={<LoadingOutlined spin />}
        spinning={loading}
      >
        <IconCustom type="language" />
      </Spin>
    ),
    children: loading ? [] : subMenu,
  };
};
